import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LOGO_URL } from "../lib/brand";
import { useAuth } from "../context/AuthContext";
import { useApp } from "../context/AppContext";

const reduce = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function greetingWord(language) {
  const h = new Date().getHours();
  const M = { हिंदी: ["सुप्रभात", "नमस्कार", "शुभ संध्या"], English: ["Good morning", "Namaste", "Good evening"], Hinglish: ["Good morning", "Namaste", "Good evening"] };
  const arr = M[language] || M["हिंदी"];
  return h < 12 ? arr[0] : h < 17 ? arr[1] : arr[2];
}

export default function WelcomeSplash({ variant = "pre" }) {
  const { user } = useAuth();
  const { language } = useApp();
  const storageKey = variant === "post" ? "am_splash_post" : "am_splash_pre";
  const [show, setShow] = useState(() => !sessionStorage.getItem(storageKey));
  const [phase, setPhase] = useState("logo"); // logo -> namaste -> exit
  const [dim] = useState(() => ({ w: typeof window !== "undefined" ? window.innerWidth : 1200, h: typeof window !== "undefined" ? window.innerHeight : 800 }));

  const finish = () => { sessionStorage.setItem(storageKey, "1"); setPhase("exit"); setTimeout(() => setShow(false), reduce ? 150 : 400); };

  useEffect(() => {
    if (!show) return undefined;
    // Hard failsafe: never let the splash block the app for more than 2s.
    const kill = setTimeout(() => { sessionStorage.setItem(storageKey, "1"); setShow(false); }, 2000);
    if (reduce) { const t = setTimeout(finish, 700); return () => { clearTimeout(t); clearTimeout(kill); }; }
    const t1 = setTimeout(() => setPhase("namaste"), 650);
    const t2 = setTimeout(finish, 1600);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(kill); };
  }, [show]);

  const firstName = (user?.name || "").trim().split(/\s+/)[0];
  const cornerX = -(dim.w / 2) + (dim.w >= 1024 ? 60 : 44);
  const cornerY = -(dim.h / 2) + 44;

  return (
    <AnimatePresence>
      {show && (
        <motion.div key="splash" data-testid="welcome-splash" onClick={finish}
          className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden font-deva"
          style={{ background: "radial-gradient(120% 120% at 50% 30%, #eafaf0 0%, #d6f2e0 45%, #bfe9cf 100%)" }}
          initial={{ opacity: 1 }} exit={{ opacity: 0, backdropFilter: "blur(16px)" }} transition={{ duration: 0.6, ease: "easeInOut" }}>

          <div className="relative flex flex-col items-center">
            {/* leaf trailing the logo on exit */}
            {!reduce && phase === "exit" && (
              <motion.span className="absolute text-lg" style={{ left: "50%", top: "50%" }}
                initial={{ opacity: 0, x: 0, y: 0 }} animate={{ opacity: [0, 0.9, 0], x: [0, cornerX * 0.5, cornerX], y: [0, cornerY * 0.5 + 8, cornerY] }}
                transition={{ duration: 0.6, ease: "easeInOut" }}>🌿</motion.span>
            )}

            <motion.img src={LOGO_URL} alt="Agro Madat" data-testid="splash-logo" layoutId="brand-logo"
              className="rounded-3xl object-contain bg-white shadow-xl"
              initial={reduce ? { scale: 1, opacity: 0 } : { scale: 0.6, opacity: 0 }}
              animate={
                phase === "logo" ? { scale: 1.4, opacity: 1, y: 0, width: 96, height: 96, boxShadow: ["0 10px 30px rgba(22,163,74,0.2)", "0 10px 50px rgba(22,163,74,0.5)", "0 10px 30px rgba(22,163,74,0.25)"] }
                : phase === "namaste" ? { scale: 1, opacity: 1, y: -20, width: 72, height: 72 }
                : { scale: 0.42, opacity: 1, x: cornerX, y: cornerY, width: 72, height: 72 }
              }
              transition={phase === "logo" ? { duration: 1, boxShadow: { duration: 1.4, repeat: Infinity } } : { duration: reduce ? 0.3 : 0.8, ease: [0.45, 0, 0.2, 1] }}
              style={{ width: 96, height: 96, willChange: "transform" }} />

            {/* wordmark during logo phase */}
            <AnimatePresence>
              {phase === "logo" && (
                <motion.p key="wm" className="mt-6 text-2xl font-bold text-emerald-800"
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ delay: 0.3 }}>
                  <span className="text-green-600">Agro</span> Madat
                </motion.p>
              )}
            </AnimatePresence>

            {/* namaste writing */}
            <AnimatePresence>
              {phase === "namaste" && (
                <motion.div key="ns" className="flex flex-col items-center mt-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <svg viewBox="0 0 600 160" className="w-[260px] sm:w-[360px] h-auto" aria-label="नमस्ते">
                    <motion.text x="300" y="115" textAnchor="middle" fontFamily="'Tiro Devanagari Hindi','Noto Sans Devanagari',serif" fontSize="120" fontWeight="600"
                      fill="none" stroke="#16a34a" strokeWidth="1.4"
                      initial={reduce ? { fillOpacity: 1, strokeDashoffset: 0 } : { strokeDasharray: 1400, strokeDashoffset: 1400, fillOpacity: 0 }}
                      animate={{ strokeDashoffset: 0, fill: "#15803d", fillOpacity: 1 }}
                      transition={reduce ? { duration: 0.3 } : { strokeDashoffset: { duration: 0.8, ease: "easeInOut" }, fillOpacity: { delay: 0.5, duration: 0.3 }, fill: { delay: 0.5, duration: 0.3 } }}>
                      नमस्ते
                    </motion.text>
                  </svg>
                  <motion.p className="text-lg sm:text-xl font-medium text-emerald-900" data-testid="splash-greeting"
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: reduce ? 0.1 : 0.55, duration: 0.35 }}>
                    {greetingWord(language)}{firstName ? `, ${firstName} जी` : " जी"}
                  </motion.p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <button data-testid="splash-skip" onClick={(e) => { e.stopPropagation(); finish(); }} className="absolute bottom-8 text-xs text-emerald-800/50 tap">Skip →</button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
